"use client";

import { useEffect, useMemo, useState } from "react";
import { getSiteContent } from "@/content";
import { type Locale } from "@/lib/site";
import { FAQItem, FieldInput } from "@/components/ui/StitchUI";

export function FaqPage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.faq;
  const [activeCategoryId, setActiveCategoryId] = useState<string>(t.categories[0]?.id ?? "");
  const [query, setQuery] = useState("");
  const activeCategory = t.categories.find((category) => category.id === activeCategoryId) ?? t.categories[0];

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;

    if (t.categories.some((category) => category.id === hash)) {
      setActiveCategoryId(hash);
      setQuery("");
      window.setTimeout(() => {
        document.getElementById("faq-content")?.scrollIntoView({ block: "start", behavior: "smooth" });
      }, 0);
    }
  }, [t.categories]);

  const searchResults = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];

    return t.categories.flatMap((category) =>
      category.questions
        .filter(([question, answer]) => `${question} ${answer}`.toLowerCase().includes(term))
        .map(([question, answer]) => ({ question, answer, category: category.title, icon: category.icon }))
    );
  }, [query, t.categories]);

  const questions = query.trim()
    ? searchResults
    : activeCategory.questions.map(([question, answer]) => ({
        question,
        answer,
        category: activeCategory.title,
        icon: activeCategory.icon
      }));

  return (
    <main className="mx-auto w-full max-w-container-max space-y-section-gap overflow-x-hidden px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <section className="mx-auto max-w-3xl space-y-stack-lg text-center">
        <h1 className="typo-page-h1 text-primary">{t.hero.title}</h1>
        <div className="ui-card flex items-center gap-3 border border-border-pale bg-white px-5 py-4 shadow-sm">
          <span className="material-symbols-outlined text-secondary">search</span>
          <FieldInput className="border-none bg-transparent p-0 shadow-none focus:border-transparent focus:ring-0" placeholder={t.hero.placeholder} value={query} onChange={(event) => setQuery(event.target.value)} />
        </div>
      </section>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {t.categories.map((category) => {
          const active = category.id === activeCategory.id && !query.trim();
          return (
            <button
              className={`ui-card flex min-h-[150px] flex-col items-center justify-center gap-3 border p-5 text-center transition-all hover:-translate-y-1 hover:border-secondary hover:shadow-lg ${
                active ? "border-primary-container bg-primary-container text-white shadow-lg" : "border-border-pale bg-white text-primary"
              }`}
              id={category.id}
              key={category.id}
              onClick={() => {
                setActiveCategoryId(category.id);
                setQuery("");
              }}
              type="button"
            >
              <span className={`material-symbols-outlined text-[34px] ${active ? "text-white" : "text-primary"}`}>{category.icon}</span>
              <span className="font-headline-md text-[18px]">{category.title}</span>
            </button>
          );
        })}
      </section>

      <section className="ui-card scroll-mt-[140px] border border-border-pale bg-white p-stack-lg shadow-sm" id="faq-content">
        <div className="mb-stack-lg flex items-center gap-4">
          <span className="ui-icon-tile ui-icon-tile-md bg-primary-container text-white">
            <span className="material-symbols-outlined">{query.trim() ? "search" : activeCategory.icon}</span>
          </span>
          <div>
            <h2 className="font-headline-md text-headline-md text-primary">{query.trim() ? "搜索结果" : activeCategory.title}</h2>
            {query.trim() ? <p className="mt-1 font-body-sm text-on-surface-variant">与“{query}”相关的问题</p> : null}
          </div>
        </div>

        {questions.length ? (
          <div className="space-y-4">
            {questions.map((item, index) => (
              <FAQItem
                answer={item.answer}
                className="rounded-none border-0 border-b border-border-pale bg-transparent px-0 shadow-none last:border-b-0"
                answerClassName="border-l-2 border-secondary-container pl-4"
                icon="expand_more"
                key={`${item.category}-${item.question}`}
                open={index === 0}
                question={item.question}
                summaryClassName="font-headline-md"
              />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center font-body-md text-outline">{t.hero.empty}</div>
        )}
      </section>
    </main>
  );
}
